import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { SOPsService } from '../../services/sops.service';
import { AuthService } from '../../services/auth.service';
import { MarkdownModule } from 'ngx-markdown';
import { SOP } from '../../interfaces/sop.interface';
import { OrganizationService } from '../../services/organization.service';
import { Department } from '../../interfaces/organization.interface';
import { switchMap, map } from 'rxjs/operators';
import { of } from 'rxjs';

@Component({
  selector: 'app-sop-viewer',
  standalone: true,
  imports: [CommonModule, MarkdownModule],
  template: `
    <div class="page-container">
      <div class="content-layout">
        <!-- Main Content -->
        <div class="main-content">
          <div *ngIf="sop" class="sop-container">
            <h1>{{ sop.title }}</h1>
            <div class="markdown-content">
              <markdown [data]="sop.content"></markdown>
            </div>
          </div>
          <div *ngIf="!sop" class="error-message">
            SOP not found.
          </div>
        </div>

        <!-- Details Sidebar -->
        <div class="details-sidebar" *ngIf="sop">
          <button 
            *ngIf="isAdmin"
            class="edit-button"
            (click)="editSOP(sop)">
            Edit SOP
          </button>
          <div class="details-card">
            <h2>SOP Details</h2>
            <div class="details-content">
              <div class="detail-item">
                <span class="label">Category:</span>
                <span class="value">{{ sop.category }}</span>
              </div>
              <div class="detail-item" *ngIf="department">
                <span class="label">Department:</span>
                <span class="value">{{ department.name }}</span>
              </div>
              <div class="detail-item">
                <span class="label">Status:</span>
                <span class="value status-badge">{{ sop.status }}</span>
              </div>
              <div class="detail-item">
                <span class="label">Last Updated:</span>
                <span class="value">{{ sop.updatedAt | date:'medium' }}</span>
              </div>
              <div class="detail-item description-item">
                <span class="label">Description:</span>
                <p class="value description">{{ sop.description }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .page-container {
      max-width: 1400px;
      margin: 0 auto;
      padding: 2rem;
    }

    .content-layout {
      display: grid;
      grid-template-columns: 1fr 320px;
      gap: 2.5rem;
      align-items: start;
    }

    .main-content {
      background: white;
      border-radius: 0.75rem;
      padding: 3rem;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      min-height: calc(100vh - 4rem);
    }

    .markdown-content {
      max-width: 800px;
      margin: 0 auto;
    }

    .markdown-content ::ng-deep {
      font-size: 1.125rem;
      line-height: 1.7;
      color: #374151;
    }

    .markdown-content ::ng-deep h1,
    .markdown-content ::ng-deep h2,
    .markdown-content ::ng-deep h3 {
      color: #111827;
      margin-top: 2rem;
      margin-bottom: 1rem;
      font-weight: 600;
    }

    .markdown-content ::ng-deep h1 {
      font-size: 2rem;
    }

    .markdown-content ::ng-deep h2 {
      font-size: 1.5rem;
    }

    .markdown-content ::ng-deep h3 {
      font-size: 1.25rem;
    }

    .markdown-content ::ng-deep p {
      margin-bottom: 1.5rem;
    }

    .markdown-content ::ng-deep ul,
    .markdown-content ::ng-deep ol {
      margin-bottom: 1.5rem;
      padding-left: 1.5rem;
    }

    .markdown-content ::ng-deep li {
      margin-bottom: 0.5rem;
    }

    .details-sidebar {
      position: sticky;
      top: 2rem;
    }

    .details-card {
      background: white;
      border-radius: 0.75rem;
      padding: 1.5rem;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    }

    h1 {
      font-size: 2.25rem;
      color: #111827;
      margin-bottom: 3rem;
      font-weight: 600;
      line-height: 1.2;
    }

    h2 {
      font-size: 1.25rem;
      color: #111827;
      margin-bottom: 1.5rem;
      font-weight: 600;
    }

    .details-content {
      display: flex;
      flex-direction: column;
      gap: 1.25rem;
    }

    .detail-item {
      display: flex;
      flex-direction: column;
      gap: 0.375rem;
    }

    .description-item {
      margin-top: 0.5rem;
      padding-top: 1rem;
      border-top: 1px solid #e5e7eb;
    }

    .label {
      font-size: 0.875rem;
      color: #6b7280;
      font-weight: 500;
    }

    .value {
      font-size: 1rem;
      color: #111827;
      font-weight: 500;
    }

    .status-badge {
      display: inline-block;
      background-color: #f0f7ff;
      color: #0066cc;
      padding: 0.375rem 0.875rem;
      border-radius: 2rem;
      font-size: 0.875rem;
      font-weight: 500;
      align-self: flex-start;
    }

    .description {
      font-weight: normal;
      line-height: 1.6;
      color: #374151;
    }

    .error-message {
      text-align: center;
      padding: 2rem;
      color: #dc2626;
      font-size: 1.125rem;
    }

    @media (max-width: 1200px) {
      .content-layout {
        grid-template-columns: 1fr 300px;
        gap: 2rem;
      }

      .main-content {
        padding: 2rem;
      }
    }

    @media (max-width: 1024px) {
      .content-layout {
        grid-template-columns: 1fr;
      }

      .details-sidebar {
        position: static;
        margin-top: 2rem;
      }

      .main-content {
        min-height: auto;
      }
    }

    @media (max-width: 768px) {
      .page-container {
        padding: 1rem;
      }

      .main-content {
        padding: 1.5rem;
      }

      h1 {
        font-size: 1.875rem;
        margin-bottom: 2rem;
      }

      .markdown-content ::ng-deep {
        font-size: 1rem;
      }
    }

    .edit-button {
      width: 100%;
      padding: 0.75rem;
      background-color: #0066cc;
      color: white;
      border: none;
      border-radius: 0.5rem;
      font-size: 0.875rem;
      font-weight: 500;
      cursor: pointer;
      transition: background-color 0.2s ease;
      margin-bottom: 1rem;
    }

    .edit-button:hover {
      background-color: #0052a3;
    }
  `]
})
export class SopViewerComponent implements OnInit {
  sop: SOP | null = null;
  department: Department | null = null;
  isAdmin = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private sopsService: SOPsService,
    private authService: AuthService,
    private organizationService: OrganizationService
  ) {}

  ngOnInit() {
    this.checkAdminStatus();
    this.loadSOP();
  }

  private checkAdminStatus(): void {
    this.authService.isAdmin().subscribe(isAdmin => {
      this.isAdmin = isAdmin;
    });
  }

  private loadSOP(): void {
    this.route.params.pipe(
      switchMap(params => this.sopsService.getSopBySlug(params['slug'])),
      switchMap(sop => {
        if (!sop) return of({ sop: null, department: null });
        return this.organizationService.getDepartmentById(sop.departmentId).pipe(
          map(department => ({ sop, department }))
        );
      })
    ).subscribe({
      next: ({ sop, department }) => {
        this.sop = sop;
        this.department = department;
      },
      error: (error) => {
        console.error('Error loading SOP:', error);
        this.router.navigate(['/not-found']);
      }
    });
  }

  editSOP(sop: SOP) {
    this.router.navigate(['/admin/sops/edit', sop.id]);
  }

  async deleteSOP() {
    if (!this.sop) return;

    if (confirm('Are you sure you want to delete this SOP?')) {
      try {
        await this.sopsService.deleteSop(this.sop.id);
        this.router.navigate(['/sops']);
      } catch (error) {
        console.error('Error deleting SOP:', error);
      }
    }
  }
} 